import React, { useState } from "react";
import api from "../axiosConfig";
import DatePicker from 'react-date-picker';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import Select from 'react-select';
import "./UserDropdown.css"; // Import CSS file
import { format } from 'date-fns';

const UserDropdown = ({ users }) => {
    const [selectedUser, setSelectedUser] = useState("");
    const [clockRecords, setClockRecords] = useState([]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize] = useState(10);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [appliedColumns, setAppliedColumns] = useState([]);

    const token = localStorage.getItem("accessToken");

    const columnOptions = [
        { value: 'cr.clock_in_time', label: 'Clock In Time' },
        { value: 'cr.clock_out_time', label: 'Clock Out Time' },
        { value: 'cr.working_duration', label: 'Working Duration' },
        { value: 'cr.overtime_clock_in', label: 'Overtime Clock In' },
        { value: 'cr.overtime_clock_out', label: 'Overtime Clock Out' },
        { value: 'cr.remarks', label: 'Remarks' },
        { value: 'u.fullname', label: 'Full Name' },
        { value: 'u.username', label: 'Username' },
        { value: 'g.name', label: 'Group Name' },
        { value: 'cover_for', label: 'Cover For' },
    ];

    const columnOrder = [
        'u.fullname',
        'g.name',
        'u.username',
        'cr.clock_in_time',
        'cr.clock_out_time',
        'cr.working_duration',
        'cr.remarks',
        'cr.overtime_clock_in',
        'cr.overtime_clock_out',
        'cover_for'
    ];

    const handleColumnChange = (selected) => {
        const sortedColumns = sortColumns(selected);
        setSelectedColumns(sortedColumns);
      };

    const sortColumns = (columns) => {
        return columns.sort((a, b) => {
          return columnOrder.indexOf(a.value) - columnOrder.indexOf(b.value);
        });
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            fetchClockRecords(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            fetchClockRecords(currentPage + 1);
        }
    };

    const exportTable = async () => {
        try {
            const formattedStartDate = startDate ? new Date(startDate).toLocaleDateString().split("T")[0] : "";
            const formattedEndDate = endDate ? new Date(endDate).toLocaleDateString().split("T")[0] : "";
            const response = await api.post(
                "/clock-record/export", 
                {   userId: selectedUser, 
                    startDate: formattedStartDate, 
                    endDate: formattedEndDate, 
                    columns: selectedColumns.map((col) => col.value) },
                { headers: { Authorization: token }, responseType: 'blob' }
            );

            // Extract the filename from the Content-Disposition header
            const contentDisposition = response.headers['content-disposition'];
            const fileName = contentDisposition
                ? contentDisposition.split('filename=')[1].replace(/['"]/g, '') // Remove quotes
                : 'clock_records.xlsx';

            // Create a download link for the Excel file
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName); // Use the filename from the backend

            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            console.log("done export");
        } catch (error) {
            console.error("Error exporting records:", error);
        }
    };

    const handleDateChange = (isEndDate, date) => {
        if (isEndDate) {
            date.setHours(23, 59, 59);
            setEndDate(date);
            return;
        }

        date.setHours(0, 0, 0)
        setStartDate(date);
        return;
    }

    const fetchClockRecords = async (page = 1) => {
        try {
            const formattedStartDate = startDate ? new Date(startDate).toISOString()  : "";         
            const formattedEndDate = endDate ? new Date(endDate).toISOString() : "";
            const response = await api.post(
                "/clock-record/list", 
                {   userId: selectedUser, 
                    startDate: formattedStartDate, 
                    endDate: formattedEndDate, 
                    skip: (page - 1) * pageSize, 
                    take: pageSize, 
                    columns: selectedColumns.map((col) => col.value) },
                { headers: { Authorization: token }}
            );
            const data = response.data.data;
            if (data) {
                setClockRecords(data);
                setTotalPages(response.data.pagination.totalPages);
                setCurrentPage(response.data.pagination.currentPage);
                setAppliedColumns(sortColumns(selectedColumns.length > 0 ? selectedColumns : columnOptions));
            } else {
                console.error("Failed to fetch users:", data.message);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    const userOptions = users.map((user) => ({
        value: user.id, // Value to be used in the form
        label: user.fullname, // Display text in the dropdown
    }));

    const columnHeaders = {
        'cr.clock_in_time': 'Clock In Time',
        'cr.clock_out_time': 'Clock Out Time',
        'cr.working_duration': 'Working Duration',
        'cr.overtime_clock_in': 'Overtime Clock In',
        'cr.overtime_clock_out': 'Overtime Clock Out',
        'cr.remarks': 'Remarks',
        'u.fullname': 'Full Name',
        'u.username': 'Username',
        'g.name': 'Group Name',
        'cover_for' : 'Cover For',
    };

    return (
        <>
            <div className="user-dropdown">
                <div className="user-dropdown-container">
                    <h1>User Clock Records</h1>

                    <div className="form-group">
                        <label htmlFor="userDropdown">Select User</label>
                        <Select
                            id="userDropdown"
                            closeMenuOnSelect={false}
                            options={userOptions}
                            value={userOptions.find(option => option.value === selectedUser)} 
                            onChange={(selectedOptions) => setSelectedUser(selectedOptions?.map(option => option.value) || [])}
                            isSearchable
                            filterOption={(option, inputValue) =>
                                option.label.toLowerCase().includes(inputValue?.toLowerCase())
                            }
                            isClearable
                            isMulti
                            className="react-select-container form-input"
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="datePicker">Start Date</label>
                        <DatePicker
                            id="datePicker"
                            onChange={(e) => handleDateChange(false, e)}
                            value={startDate}
                            format="dd-MM-yyyy"
                            className="form-input"
                            inputProps={{ readOnly: true }}
                        />
                        <label htmlFor="datePicker">End Date</label>
                        <DatePicker
                            id="datePicker"
                            onChange={(e) => handleDateChange(true, e)}
                            value={endDate}
                            format="dd-MM-yyyy"
                            className="form-input"
                            inputProps={{ readOnly: true }}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="columnDropdown">Select Columns</label>
                        <Select
                            id="columnDropdown"
                            closeMenuOnSelect={false}
                            options={columnOptions}
                            isMulti
                            isClearable
                            value={selectedColumns} 
                            onChange={handleColumnChange}
                            placeholder="Select columns..."
                            className="react-select-container form-input"
                            classNamePrefix="react-select"
                            />
                    </div>

                    <div className="form-group">
                        <button
                            onClick={fetchClockRecords}
                            className="search-button"
                        >
                            Search
                        </button>
                        {clockRecords.length > 0 && (
                            <button
                                onClick={exportTable}
                                className="export-button"
                            >
                                Export Table
                            </button>
                        )}
                    </div>
                </div>
            </div>
            
            {clockRecords.length > 0 ? (
                <div className="table-container">
                    <table className="clock-records-table">
                        <thead>
                            <tr>
                            {appliedColumns.map((column) => (
                                <th key={column.value}>{columnHeaders[column.value]}</th>
                            ))}
                            </tr>
                        </thead>
                        <tbody>
                            {clockRecords.map((record, index) => (
                            <tr key={index}>
                                {appliedColumns.map((column) => {
                                return (
                                    <td key={column.value}>
                                        {
                                            column.value === 'cr.clock_in_time'
                                            ? format(record.clock_in_time, 'dd/MM/yyyy, h:mm:ss a')
                                            : column.value === 'cr.clock_out_time'
                                            ? (record.clock_out_time ? format(record.clock_out_time, 'dd/MM/yyyy, h:mm:ss a') : "N/A")
                                            : column.value === 'cr.overtime_clock_in'
                                            ? (record.overtime_clock_in ? format(new Date(record.overtime_clock_in), 'dd/MM/yyyy, h:mm:ss a') : "-")
                                            : column.value === 'cr.overtime_clock_out'
                                            ? (record.overtime_clock_out ? format(new Date(record.overtime_clock_out), 'dd/MM/yyyy, h:mm:ss a') : "-")
                                            : column.value === 'cover_for'
                                            ? (record.cover_for || "-")
                                            : record[column.value.split('.')[1]] ?? "-"
                                        }
                                    </td>
                                );
                                })}
                            </tr>
                            ))}
                        </tbody>
                    </table>

                     <div className="pagination">
                         <button
                            onClick={handlePrevPage}
                            disabled={currentPage === 1}
                            className="pagination-button"
                        >
                            Prev
                        </button>
                        <span>Page {currentPage} of {totalPages}</span>
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            className="pagination-button"
                        >
                            Next
                        </button>
                    </div>

                </div>
                ) : (
                    <div className="no-records">
                        <p>No clock records available.</p>
                    </div>
                )}
        </>
    );
};

export default UserDropdown;